import React, { useCallback } from 'react';
import { Field, useFormikContext } from 'formik';
import styled from 'styled-components';

import { FormGroup } from 'components/bootstrap';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import { Select, Icon, Spinner } from 'components/common';
import type { DataLakeTableOption, DataLakeTableOptions } from 'data-lake/preview/Types';
import useUpdateTableFilter from 'data-lake/preview/storage-types/external/hooks/useUpdateTableFilter';

const Container = styled(FormGroup)`
  flex: 1;
`;

const placeholder = 'Select the table you want to preview logs for';

type Props = {
  tables: DataLakeTableOptions;
  isLoading: boolean;
  hasOnlyOneCatalog: boolean;
};

const TableFilter = ({ tables, isLoading, hasOnlyOneCatalog }: Props) => {
  const { dataLakeFieldsFetchQuery } = useSelectedDataLake();
  const { setFieldValue } = useFormikContext();
  const updateTableFilter = useUpdateTableFilter(setFieldValue);

  const onChange = useCallback(
    (newTable: string) => {
      updateTableFilter(newTable);
    },
    [updateTableFilter],
  );

  const renderOption = useCallback(
    (option: DataLakeTableOption) => {
      if (hasOnlyOneCatalog) {
        return <>{option.label}</>;
      }

      return (
        <>
          {option.catalog} <Icon name="chevron_right" /> {option.schema} <Icon name="chevron_right" />{' '}
          <b>{option.label}</b>
        </>
      );
    },
    [hasOnlyOneCatalog],
  );

  return (
    <Field name="table" value={dataLakeFieldsFetchQuery?.tableId}>
      {({ meta: { error }, field: { value } }) => (
        <Container data-testid="table-filter" validationState={error ? 'error' : undefined} bsClass="no-bm">
          {isLoading ? (
            <Spinner text="Loading tables..." />
          ) : (
            <Select
              placeholder={placeholder}
              displayKey="key"
              inputId="table-filter"
              value={value}
              optionRenderer={renderOption}
              options={tables}
              multi={false}
              onChange={onChange}
            />
          )}
        </Container>
      )}
    </Field>
  );
};

export default TableFilter;
